import {
  DATE_RELATIONS,
  type FilterOption,
  type DateRelation,
} from '@frontend/api';
import {
  InputGroup,
  InputLeftAddon,
  VStack,
} from '@frontend/legacy-components';

import { valueUsesAmountAndUnitInput } from './DateRelationFilter/DateRelationFilter.util';
import IsInclusiveCheckbox from './DateRelationFilter/IsInclusiveCheckbox';
import MainDateAndTimeInputs from './DateRelationFilter/MainDateAndTimeInputs';
import RelationSelector, {
  RelationSelectorProps,
} from './DateRelationFilter/RelationSelector';
import { DATE_UNIT_PLURAL_TO_DEFAULT_AMOUNT_MAP } from './DateRelationFilter/const/dateUnitPluralToDefaultAmountMap.const';
import { TimeUnitPlural, TimeUnitSingular } from './DateRelationFilter/types';
import type {
  ExcludableDateRelation,
  RelationFilterValue,
  RelationFilterWorkingValue,
  RelationFilterWorkingValueByRelation,
  RelationSelectorOption,
} from './DateRelationFilter/types';

const DEFAULT_DATE_UNIT_PLURAL = TimeUnitPlural.DAYS;

const DEFAULT_IS_INCLUSIVE_VALUE = false;

const RELATION_TO_INITIAL_VALUE_MAP: {
  [Relation in DateRelation]: RelationFilterWorkingValueByRelation<Relation>;
} = {
  [DATE_RELATIONS.ON]: {
    relation: DATE_RELATIONS.ON,
    startDate: null,
  },
  [DATE_RELATIONS.BEFORE]: {
    relation: DATE_RELATIONS.BEFORE,
    startDate: null,
    relativeDate: '',
  },
  [DATE_RELATIONS.AFTER]: {
    relation: DATE_RELATIONS.AFTER,
    startDate: null,
    relativeDate: '',
  },
  [DATE_RELATIONS.BETWEEN]: {
    relation: DATE_RELATIONS.BETWEEN,
    startDate: null,
    endDate: null,
  },
  [DATE_RELATIONS.IN_THE_LAST]: {
    relation: DATE_RELATIONS.IN_THE_LAST,
    amount: DATE_UNIT_PLURAL_TO_DEFAULT_AMOUNT_MAP[DEFAULT_DATE_UNIT_PLURAL],
    unit: DEFAULT_DATE_UNIT_PLURAL,
    isInclusive: DEFAULT_IS_INCLUSIVE_VALUE,
  },
  [DATE_RELATIONS.IN_THE_NEXT]: {
    relation: DATE_RELATIONS.IN_THE_NEXT,
    amount: DATE_UNIT_PLURAL_TO_DEFAULT_AMOUNT_MAP[DEFAULT_DATE_UNIT_PLURAL],
    unit: DEFAULT_DATE_UNIT_PLURAL,
    isInclusive: DEFAULT_IS_INCLUSIVE_VALUE,
  },
  [DATE_RELATIONS.IN_THIS_HOUR]: {
    relation: DATE_RELATIONS.IN_THIS_HOUR,
    unit: TimeUnitSingular.HOUR,
  },
  [DATE_RELATIONS.TODAY]: {
    relation: DATE_RELATIONS.TODAY,
    unit: TimeUnitSingular.DAY,
  },
  [DATE_RELATIONS.IN_THIS_WEEK]: {
    relation: DATE_RELATIONS.IN_THIS_WEEK,
    unit: TimeUnitSingular.WEEK,
  },
  [DATE_RELATIONS.IN_THIS_MONTH]: {
    relation: DATE_RELATIONS.IN_THIS_MONTH,
    unit: TimeUnitSingular.MONTH,
  },
  [DATE_RELATIONS.IN_THIS_YEAR]: {
    relation: DATE_RELATIONS.IN_THIS_YEAR,
    unit: TimeUnitSingular.YEAR,
  },
  [DATE_RELATIONS.NEVER]: {
    relation: DATE_RELATIONS.NEVER,
    unit: TimeUnitSingular.NEVER,
  },
};

type WithExcludedRelationsProp<
  BaseType,
  ExcludedRelations extends ExcludableDateRelation,
> = [ExcludedRelations] extends [never]
  ? BaseType & {
      excludedRelations?: { [k in ExcludableDateRelation]?: false };
    }
  : BaseType & {
      excludedRelations: {
        [k in Exclude<ExcludableDateRelation, ExcludedRelations>]?: false;
      } & {
        [k in ExcludedRelations]: true;
      };
    };

export type NewDateFilterProps<
  ExcludedRelations extends ExcludableDateRelation,
> = WithExcludedRelationsProp<
  {
    name?: string;
    options: FilterOption[];
    value: RelationFilterValue<ExcludedRelations>;
    relationSelectRef?: React.RefObject<unknown>;
    relationSelectorProps?: Omit<
      RelationSelectorProps<Exclude<DateRelation, ExcludedRelations>>,
      'name' | 'options' | 'value' | 'defaultValue' | 'onChange'
    >;
    isPopoverOverlayEnabled?: boolean;
    onChange: (newValue: RelationFilterWorkingValue<ExcludedRelations>) => void;
  },
  ExcludedRelations
>;

export const NewDateFilter = <
  ExcludedRelations extends ExcludableDateRelation,
>({
  name = 'dateRelationFilter',
  options,
  value,
  relationSelectRef,
  relationSelectorProps,
  isPopoverOverlayEnabled,
  onChange,
}: NewDateFilterProps<ExcludedRelations>) => {
  type NonExcludedRelation = Exclude<DateRelation, ExcludedRelations>;
  const defaultRelation = DATE_RELATIONS.ON as NonExcludedRelation;

  const getInitialValueByRelation = <Relation extends NonExcludedRelation>(
    relation: Relation,
  ): RelationFilterWorkingValueByRelation<Relation, ExcludedRelations> =>
    RELATION_TO_INITIAL_VALUE_MAP[
      relation
    ] as unknown as RelationFilterWorkingValueByRelation<
      Relation,
      ExcludedRelations
    >;

  const workingValue: RelationFilterWorkingValue<ExcludedRelations> =
    value ?? getInitialValueByRelation(defaultRelation);

  const relationSelectorOptions: RelationSelectorOption[] =
    options as RelationSelectorOption[];

  const findRelationSelectorOptionByValue = (
    value: RelationSelectorOption['value'],
  ): RelationSelectorOption | undefined =>
    relationSelectorOptions.find((option) => option.value === value);

  const relationSelectorDefaultValue = findRelationSelectorOptionByValue(
    defaultRelation,
  ) as RelationSelectorOption;

  return (
    <VStack width="100%" alignItems="end">
      <InputGroup>
        <InputLeftAddon flexBasis="160px" flexShrink={0} p={0} borderWidth={0}>
          <RelationSelector
            ref={relationSelectRef}
            name={name}
            options={relationSelectorOptions}
            value={findRelationSelectorOptionByValue(workingValue.relation)}
            defaultValue={relationSelectorDefaultValue}
            onChange={(selectedOption) => {
              const selectedRelation = (
                selectedOption ?? relationSelectorDefaultValue
              ).value;

              onChange(getInitialValueByRelation(selectedRelation));
            }}
            isPopoverOverlayEnabled={isPopoverOverlayEnabled}
            {...relationSelectorProps}
          />
        </InputLeftAddon>
        <MainDateAndTimeInputs
          relationFilterValue={workingValue}
          onChange={onChange}
          useNewBeforeAndAfter
          isPopoverOverlayEnabled={isPopoverOverlayEnabled}
          blockFutureSelections={
            workingValue.relation === DATE_RELATIONS.AFTER &&
            options.every(
              (option) => option.value !== DATE_RELATIONS.IN_THE_NEXT,
            )
          }
        />
      </InputGroup>
      {valueUsesAmountAndUnitInput(workingValue) ? (
        <IsInclusiveCheckbox
          isChecked={workingValue.isInclusive}
          onChange={(event) =>
            onChange({
              ...workingValue,
              isInclusive: event.target.checked,
            })
          }
          unit={workingValue.unit}
        />
      ) : null}
    </VStack>
  );
};
